// src/hooks/useTabQueryParam.ts
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Keeps a tab selection (expressed as a string id, not a numeric index) in
 * sync with a URL query param, so refreshing the page - or using browser
 * Back/Forward - restores whichever tab was active. This generalizes the
 * pattern ProfilePage originally established for its own `?tab=notifications`
 * param, so SettingsPage/PetSettings/RecycleBin (and now ProfilePage itself)
 * share one implementation instead of four copies of the same boilerplate.
 *
 * Ids rather than indices: `?tab=pets` survives a future tab reorder,
 * `?tab=2` would silently point at the wrong panel.
 *
 * The default tab is omitted from the URL entirely (clean `/settings`
 * rather than `/settings?tab=system`) - same convention ProfilePage already
 * used for its default (account) tab.
 *
 * An id that isn't in `validIds` (stale bookmark, hand-edited URL, a tab
 * that's since been removed) silently falls back to `defaultId` rather than
 * breaking whatever renders based on it.
 *
 * Every write uses `{ replace: true }`, so clicking between tabs doesn't
 * spam the browser history stack with one entry per click - only the
 * initial navigation to the page is a "real" history entry.
 *
 * Caution: if a single event handler needs to update *two* params backed
 * by two separate instances of this hook (SettingsPage's `tab` +
 * `petSubTab` shortcut is the one place this happens today), calling both
 * setters back-to-back will lose one of the writes - each setter builds
 * its next URLSearchParams from its own render-memoized snapshot, and
 * react-router doesn't merge concurrent setSearchParams calls. For that
 * case, write both params in a single combined setSearchParams call
 * instead of going through two hook instances - see SettingsPage.tsx's
 * handleTabChange.
 */
export function useTabQueryParam<T extends string>(
  paramName: string,
  validIds: readonly T[],
  defaultId: T
): [T, (id: T) => void] {
  const [searchParams, setSearchParams] = useSearchParams();

  const resolve = (raw: string | null): T =>
    raw && (validIds as readonly string[]).includes(raw)
      ? (raw as T)
      : defaultId;

  const [tab, setTab] = useState<T>(() => resolve(searchParams.get(paramName)));

  useEffect(() => {
    setTab(resolve(searchParams.get(paramName)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, paramName]);

  const setTabId = (id: T) => {
    const resolved = resolve(id);
    setTab(resolved);
    const next = new URLSearchParams(searchParams);
    if (resolved === defaultId) {
      next.delete(paramName);
    } else {
      next.set(paramName, resolved);
    }
    setSearchParams(next, { replace: true });
  };

  return [tab, setTabId];
}
